import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { SourceId } from '../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import React from 'react'
import { useSelector } from '../../../Flux/index.ts'
import type { State } from '../../../Flux/Slice/index.ts'
import { DocumentChartBarIcon } from '@heroicons/react/16/solid'
import { TooltipTrigger } from '../../../Provider/TooltipManager.tsx'
import { selectCubeContext } from './Sinks/Selector/CubeContext.ts'
import { buildSourceTable } from './DataTable/SourceTable.ts'
import { csvFilename, downloadCsv } from './DataTable/SinkTable.ts'

const sinkCountLabel = (count: number) => {
  if (count === 1) return '1 sink'
  return `${count} sinks`
}

export default function SourceTabs({
  sourceIds,
  activeId,
  onSelect,
  onDelete,
  onRename
}: {
  sourceIds: SourceId[]
  activeId: SourceId | null
  onSelect: (id: SourceId) => void
  onDelete: (id: SourceId) => void
  onRename: (id: SourceId, newName: string) => void
}) {
  const [mouseOverElementId, setMouseOverElementId] = React.useState<SourceId | null>(null)
  const [editName, setEditName] = React.useState<Record<SourceId, string>>({})

  const handleMouseOverElement = (id: SourceId) => {
    setMouseOverElementId(id)
  }

  const handleMouseOut = () => {
    setMouseOverElementId(null)
  }

  const handleAcceptNewName = (sid: SourceId) => {
    onRename(sid, editName[sid])
    removeEditName(sid)
  }

  const removeEditName = (sid: SourceId) => {
    const newEditName = { ...editName }
    delete newEditName[sid]
    setEditName(newEditName)
  }

  const handleKeyDown = (e: React.KeyboardEvent, sid: SourceId) => {
    if (e.key === 'Enter') {
      handleAcceptNewName(sid)
    }
    if (e.key === 'Escape') {
      removeEditName(sid)
    }
  }

  return (
    <div className="flex flex-col flex-wrap gap-2" onMouseOut={handleMouseOut}>
      {sourceIds.map((sid) => (
        <div key={sid} className={'flex flex-col'}>
          <div className="flex items-center justify-between gap-1" onMouseOver={() => handleMouseOverElement(sid)}>
            {editName[sid] != null ? (
              <>
                <input
                  className="input input-md"
                  value={editName[sid]}
                  onChange={(e) => setEditName({ ...editName, [sid]: e.target.value })}
                  onKeyDown={(e) => handleKeyDown(e, sid)}
                />
                <div className={'flex flex-row gap-1'}>
                  <button className="btn btn-xs btn-primary" onClick={() => handleAcceptNewName(sid)}>
                    🗸
                  </button>
                  <button className="btn btn-xs" onClick={() => removeEditName(sid)}>
                    ❌
                  </button>
                </div>
              </>
            ) : (
              <SingleTab
                mouseOverElementId={mouseOverElementId}
                sid={sid}
                activeId={activeId}
                onSelect={onSelect}
                onDelete={onDelete}
                editName={editName}
                setEditName={setEditName}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

interface SingleTabProps {
  mouseOverElementId: SourceId | null
  sid: SourceId
  activeId: SourceId | null
  onSelect: (id: SourceId) => void
  onDelete: (id: SourceId) => void
  editName: Record<SourceId, string>
  setEditName: (name: Record<SourceId, string>) => void
}

const SingleTab = ({ mouseOverElementId, sid, activeId, onSelect, onDelete, editName, setEditName }: SingleTabProps) => {
  const source = useSelector((state: typeof State) => state.visualization.sources[sid])
  const cube = useSelector((state: typeof State) => selectCubeContext(state, sid))
  const canExport = cube.status === 'ready' && source?.snapshot != null

  const doExport = () => {
    if (cube.status !== 'ready' || !source.snapshot) return
    const table = buildSourceTable({
      table: cube.table,
      buckets: source.snapshot.allBuckets,
      selectedBucketKeys: new Set(source.snapshot.selectedBuckets.map((b) => b.key))
    })
    downloadCsv(table, csvFilename(`${source.name ?? sid} ${source.shared.blastType}`))
  }

  const startEdit = () => {
    setEditName({ ...editName, [sid]: source.name })
  }

  const triggerDelete = () => {
    onDelete(sid)
  }

  if (!source) {
    return <>Unknown source</>
  }

  return (
    <>
      <button
        data-testid={`source-tab-${sid}`}
        className={`btn grow inline-flex justify-between items-center ${activeId === sid ? 'btn-active bg-primary' : 'btn-outline'}`}
        onClick={() => onSelect(sid)}
      >
        <span>{source.name ?? sid}</span>
        {isLoadingState(source.status) && <span className="loading loading-dots loading-xs ml-2"></span>}
        <span className={'text-xs'}>{sinkCountLabel(Object.values(source.sinks ?? {}).length)}</span>
      </button>
      <div className={`transition-all ${mouseOverElementId === sid ? 'w-24' : 'w-0'} flex flex-row overflow-hidden`}>
        <button className="btn btn-xs mt-1 btn-ghost btn-error" onClick={triggerDelete}>
          <TrashIcon className="w-4 h-4" />
        </button>
        <button className="btn btn-xs btn-ghost mt-1" onClick={startEdit}>
          <PencilIcon className="w-4 h-4" />
        </button>
        <button className={'btn btn-xs btn-ghost mt-1'} onClick={doExport} disabled={!canExport}>
          <TooltipTrigger tooltip="Download Source Data as CSV">
            <DocumentChartBarIcon className={'w-4 h-4'} />
          </TooltipTrigger>
        </button>
      </div>
    </>
  )
}
